import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../../lib/api';
import { ShieldAlert, Loader2 } from 'lucide-react';

export default function AccessGuard({ children }) {
  const location = useLocation();
  const [state, setState] = useState({ checked: false, allowed: true, reason: '', country: '', ip: '' });

  useEffect(() => {
    // Admins can always reach admin pages (backend evaluates but admin login page needs to render either way).
    let cancelled = false;
    // Hard client-side timeout — under no circumstances should the whole app spin
    // for more than a few seconds waiting for a geo/VPN check. If the API is
    // slow or unreachable, we fail open and render the site.
    const failOpen = setTimeout(() => {
      if (!cancelled) setState({ checked: true, allowed: true, reason: '', country: '', ip: '' });
    }, 3000);
    api.get('/access-check', { timeout: 2500 })
      .then((r) => { if (!cancelled) { clearTimeout(failOpen); setState({ checked: true, allowed: r.data.allowed !== false, reason: r.data.reason || '', country: r.data.country || '', ip: r.data.ip || '' }); } })
      .catch(() => { if (!cancelled) { clearTimeout(failOpen); setState({ checked: true, allowed: true, reason: '', country: '', ip: '' }); } });
    return () => { cancelled = true; clearTimeout(failOpen); };
  }, []);

  // Never block admin routes or unsubscribe (unsubscribe must always work for compliance).
  const bypass = location.pathname.startsWith('/admin') || location.pathname === '/unsubscribe';

  if (!state.checked && !bypass) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!state.allowed && !bypass) {
    const isVpn = state.reason === 'vpn';
    return (
      <div data-testid="access-restricted" className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center p-5">
        <div className="max-w-md w-full text-center rounded-2xl bg-white border border-slate-200 shadow-xl p-8">
          <div className="w-14 h-14 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto">
            <ShieldAlert className="w-7 h-7 text-red-600" />
          </div>
          <h1 className="font-serif-display mt-5 text-2xl font-bold text-slate-900">Access Restricted</h1>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            {isVpn
              ? 'This site is not accessible over VPN, proxy, or anonymized networks. Please disable your VPN and refresh the page.'
              : `We're sorry, The Loan Alliance is not available in your region${state.country ? ` (${state.country})` : ''}.`}
          </p>
          {state.ip && <p className="mt-4 text-[11px] text-slate-400">Request reference: {state.ip}</p>}
          <p className="mt-6 text-xs text-slate-500">
            If you believe this is a mistake, please contact <a href="mailto:info@theloanalliance.com" className="text-emerald-700 underline">info@theloanalliance.com</a>.
          </p>
        </div>
      </div>
    );
  }

  return children;
}
